// RITA-1498 (Nutzungserfassung Phase 1b) — Gerät, Umgebung und Sitzungs-Momentaufnahme.
//
// Datensparsamkeit (Konzept 3.2/3.8): kein kompletter User-Agent, keine IP. Nur Browser-Name
// mit Hauptversion, Betriebssystem-Familie, Fenster-/Bildschirmmaße, Touch, Sprache, Zeitzone
// und die Darstellungseinstellungen. Alle Werte werden auf die Servergrenzen
// (batchSchema.sessionSchema, strict) gekürzt bzw. geklemmt, damit ein Bündel nie an einem
// Randwert scheitert.
import type {
  IUsageSessionSnapshot,
  UsageDeviceClass,
  UsageEnv,
  UsageThemeContrast,
  UsageThemeLayout,
  UsageThemeMode,
} from 'global-intern-types';
import type { SettingsValueProps } from '../../../components/settings/types';

export interface BrowserInfo {
  name: string;
  major: number;
}

const BROWSER_RULES: ReadonlyArray<readonly [string, RegExp]> = [
  ['Edge', /Edg(?:e|A|iOS)?\/(\d+)/],
  ['Opera', /OPR\/(\d+)/],
  ['Chrome', /(?:Chrome|CriOS)\/(\d+)/],
  ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
  ['Safari', /Version\/(\d+)[.\d]* .*Safari/],
];

export const parseBrowser = (userAgent: string): BrowserInfo => {
  const ua = userAgent || '';
  const hit = BROWSER_RULES.map(([name, rule]) => ({ name, match: rule.exec(ua) })).find((entry) => entry.match);
  if (!hit || !hit.match) return { name: 'Andere', major: 0 };
  return { name: hit.name, major: Number(hit.match[1]) || 0 };
};

export const parseOs = (userAgent: string): string => {
  const ua = userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'macOS';
  if (/Linux|X11/.test(ua)) return 'Linux';
  return 'Andere';
};

// MUI-Breakpoints: sm = 600, lg = 1200 (theme/breakpoints).
export const deviceClass = (innerWidth: number): UsageDeviceClass => {
  if (innerWidth < 600) return 'phone';
  if (innerWidth < 1200) return 'tablet';
  return 'desktop';
};

// Entscheidung 9 (nur Produktion messen) wird serverseitig durch den Schalter erzwungen; das
// Umgebungs-Etikett trennt zusätzlich lokale und Railway-Vorschau-Daten von echten Prod-Daten.
const DEV_HOSTNAME = /^(localhost|127\.0\.0\.1|\[::1\]|0\.0\.0\.0)$|\.railway\.app$/i;

export const resolveEnv = ({ hostname, nodeEnv }: { hostname: string; nodeEnv: string | undefined }): UsageEnv =>
  nodeEnv === 'production' && !DEV_HOSTNAME.test(hostname || '') ? 'prod' : 'dev';

export interface SnapshotWindow {
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
  screen: { width: number; height: number };
}

export interface SnapshotNavigator {
  userAgent: string;
  language: string;
  maxTouchPoints: number;
}

export type SnapshotSettings = Pick<
  SettingsValueProps,
  'themeMode' | 'themeLayout' | 'themeContrast' | 'themeColorPresets' | 'themeStretch'
>;

export interface SessionIdentity {
  session_id: string;
  started_at: number;
}

export interface BuildSnapshotInput {
  session: SessionIdentity;
  settings: SnapshotSettings;
  env: UsageEnv;
  appVersion: string;
  win: SnapshotWindow;
  nav: SnapshotNavigator;
  tzOffset: number;
}

export const UNKNOWN_APP_VERSION = 'unbekannt';

const clampInt = (value: unknown, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.round(Number(value) || 0)));

const truncate = (value: unknown, max: number): string => String(value ?? '').slice(0, max);

const pickEnum = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const THEME_MODES: readonly UsageThemeMode[] = ['light', 'dark'];
const THEME_LAYOUTS: readonly UsageThemeLayout[] = ['vertical', 'horizontal', 'mini'];
const THEME_CONTRASTS: readonly UsageThemeContrast[] = ['default', 'bold'];

export const buildSessionSnapshot = ({
  session,
  settings,
  env,
  appVersion,
  win,
  nav,
  tzOffset,
}: BuildSnapshotInput): IUsageSessionSnapshot => {
  const browser = parseBrowser(nav.userAgent);
  return {
    session_id: session.session_id,
    started_at: session.started_at,
    app_version: truncate(appVersion, 40) || UNKNOWN_APP_VERSION,
    env,
    browser: truncate(browser.name, 32),
    browser_major: clampInt(browser.major, 0, 32767),
    os: truncate(parseOs(nav.userAgent), 32),
    device_class: deviceClass(win.innerWidth),
    viewport_w: clampInt(win.innerWidth, 0, 65535),
    viewport_h: clampInt(win.innerHeight, 0, 65535),
    screen_w: clampInt(win.screen?.width, 0, 65535),
    screen_h: clampInt(win.screen?.height, 0, 65535),
    dpr: Math.min(99.99, Math.max(0, Math.round((Number(win.devicePixelRatio) || 1) * 100) / 100)),
    touch: (Number(nav.maxTouchPoints) || 0) > 0,
    lang: truncate(nav.language || 'unbekannt', 16),
    tz_offset: clampInt(tzOffset, -1440, 1440),
    theme_mode: pickEnum(settings.themeMode, THEME_MODES, 'light'),
    theme_layout: pickEnum(settings.themeLayout, THEME_LAYOUTS, 'vertical'),
    theme_contrast: pickEnum(settings.themeContrast, THEME_CONTRASTS, 'default'),
    theme_preset: truncate(settings.themeColorPresets || 'default', 16),
    theme_stretch: Boolean(settings.themeStretch),
  };
};
